import type React from "react";
import type { BooksSearchSchema } from "./search";

import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useBooks } from "../../../app/books";

export const Pagination: React.FC = () => {
  const { t } = useTranslation();

  const search: BooksSearchSchema = useSearch({ from: "/books" });
  const {
    pagination: { page, pages, startIndex, endIndex, total }
  } = useBooks(search);

  const visiblePages = getVisiblePages(page, pages);
  const isFirstPage = page === 1;
  const isLastPage = page === pages;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Info text */}
      <div className="text-base-content/70 text-sm">
        {t("books.pagination.summary", { start: startIndex + 1, end: endIndex, total })}
      </div>

      {/* Pagination controls */}
      <div className="dui-join">
        {/* First button - hidden on mobile */}
        <Link
          to="/books"
          className={`dui-join-item dui-btn dui-btn-sm sm:dui-btn-md hidden sm:inline-flex ${isFirstPage ? "dui-btn-disabled" : "dui-btn-outline"}`}
          search={{ ...search, page: 1 }}
          disabled={isFirstPage}
        >
          {t("books.pagination.first")}
        </Link>

        {/* Previous button */}
        <Link
          to="/books"
          className={`dui-join-item dui-btn dui-btn-sm sm:dui-btn-md ${isFirstPage ? "dui-btn-disabled" : "dui-btn-outline"}`}
          search={{ ...search, page: page - 1 }}
          disabled={isFirstPage}
        >
          {t("books.pagination.previous")}
        </Link>

        {/* Page numbers */}
        {visiblePages.map((pageNum, index) => {
          if (pageNum === "...") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="dui-join-item dui-btn dui-btn-sm sm:dui-btn-md dui-btn-disabled"
              >
                ...
              </span>
            );
          }

          return (
            <Link
              to="/books"
              key={pageNum}
              className={`dui-join-item dui-btn dui-btn-sm sm:dui-btn-md ${
                pageNum === page ? "dui-btn-active dui-btn-primary" : "dui-btn-outline"
              }`}
              search={{ ...search, page: pageNum }}
            >
              {pageNum}
            </Link>
          );
        })}

        {/* Next button */}
        <Link
          to="/books"
          className={`dui-join-item dui-btn dui-btn-sm sm:dui-btn-md ${isLastPage ? "dui-btn-disabled" : "dui-btn-outline"}`}
          search={{ ...search, page: page + 1 }}
          disabled={isLastPage}
        >
          {t("books.pagination.next")}
        </Link>

        {/* Last button - hidden on mobile */}
        <Link
          to="/books"
          className={`dui-join-item dui-btn dui-btn-sm sm:dui-btn-md hidden sm:inline-flex ${isLastPage ? "dui-btn-disabled" : "dui-btn-outline"}`}
          search={{ ...search, page: pages }}
          disabled={isLastPage}
        >
          {t("books.pagination.last")}
        </Link>
      </div>
    </div>
  );
};

function getVisiblePages(page: number, pages: number) {
  const delta = 2;
  const start = Math.max(1, page - delta);
  const end = Math.min(pages, page + delta);

  const visiblePages = [];

  // Add ellipsis at the beginning if there's a gap from page 1
  if (start > 1) {
    visiblePages.push("...");
  }

  // Add the range around current page
  for (let i = start; i <= end; i++) {
    visiblePages.push(i);
  }

  // Add ellipsis at the end if there's a gap to the last page
  if (end < pages) {
    visiblePages.push("...");
  }

  return visiblePages;
}
